import type { ReactNode } from "react";
import { createContext, useContext, useState, useCallback } from "react";
import { storage } from "@/lib/local-storage";

interface SidebarContextType {
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
    toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = (): SidebarContextType => {
    const context = useContext(SidebarContext);

    if (context === undefined) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }

    return context;
};

interface SidebarProviderProps {
    children: ReactNode;
    /**
     * The key to use to store the sidebar state in localStorage
     * @default "sidebar-collapsed"
     */
    storageKey?: string;
    /**
     * The default collapsed state
     * @default false
     */
    defaultCollapsed?: boolean;
}

export const SidebarProvider = ({
    children,
    storageKey = "sidebar-collapsed",
    defaultCollapsed = false,
}: SidebarProviderProps) => {
    const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => {
        if (typeof window !== "undefined") {
            const saved = storage.getItem(storageKey);
            return saved === "true" ? true : defaultCollapsed;
        }
        return defaultCollapsed;
    });

    const setIsCollapsed = useCallback((collapsed: boolean) => {
        setIsCollapsedState(collapsed);
        if (typeof window !== "undefined") {
            storage.setItem(storageKey, String(collapsed));
        }
    }, [storageKey]);

    const toggleSidebar = useCallback(() => {
        setIsCollapsed(!isCollapsed);
    }, [isCollapsed, setIsCollapsed]);

    return (
        <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, toggleSidebar }}>
            {children}
        </SidebarContext.Provider>
    );
};
